import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppError } from "../../../../../store/userSlice";
import SmartDownloadPagination from "../../../../../components/SmartDataTable/SmartDownloadPagination";
import ErePopup from "./ErePopup";
import EreExtensionPopUp from "./EreExtensionPopUp";
import IdlePopUp from "./IdlePopUp";
import nodeinstance from "../../../../../nodeInstance";
import { checkAccessFn } from "../../../../../utils/checkAccess";
import {
  ERE_COLUMNS,
  ERE_CONFIG,
  ERE_SEARCHABLE_FIELDS,
  formatDate,
  renderEreStatus,
  renderActionButton,
  ERE_STATUS_TYPES,
  ERE_EXTENSION_STATUSES
} from './EreTabListConstants';

const EreTabList = ({
  titles = [], // unused - can be removed
  list = [], // unused - will use SmartDownloadPagination data
  setTrigger,
  trigger,
}) => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);
  
  // Get token from localStorage (consider using context for better performance)
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  // Data tracking state for UI optimization
  const [dataState, setDataState] = useState(() => ({
    hasData: false,
    isLoading: true,
    totalRecords: 0
  }));

  // Popup state management
  const [popupState, setPopupState] = useState({
    ere: false,
    ereExtension: false,
    idle: false
  });

  const [popUpData, setPopUpData] = useState(null);

  // Data load handler to track data availability
  const handleDataLoad = useCallback((data, pagination) => {
    const dataExists = data && data.length > 0;
    setDataState({
      hasData: dataExists,
      isLoading: pagination.isLoading,
      totalRecords: pagination.totalRecords || 0
    });
    
    // Clear popup data if no data
    if (!dataExists) {
      setPopUpData(null);
    }
  }, []);

  // Optimized popup handlers
  const handleEreClick = useCallback((item) => {
    setPopUpData(item);
    setPopupState(prev => ({ ...prev, ere: true }));
  }, []);

  const handleEreExtendClick = useCallback((item) => {
    setPopUpData(item);
    setPopupState(prev => ({ ...prev, ereExtension: true }));
  }, []);

  const handleIdleClick = useCallback((item) => {
    setPopUpData(item);
    setPopupState(prev => ({ ...prev, idle: true }));
  }, []);

  // Close popup handlers
  const closeErePopup = useCallback(() => {
    setPopupState(prev => ({ ...prev, ere: false }));
    setPopUpData(null);
  }, []);

  const closeEreExtensionPopup = useCallback(() => {
    setPopupState(prev => ({ ...prev, ereExtension: false }));
    setPopUpData(null);
  }, []);

  const closeIdlePopup = useCallback(() => {
    setPopupState(prev => ({ ...prev, idle: false }));
    setPopUpData(null);
  }, []);

  // Optimized custom cell renderer
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    switch (column.key) {
      case 'serial':
        return (currentPage - 1) * pageSize + rowIndex + 1;
      
      case 'visa_provider':
        return item.visa_provider || item.Overseas_visa_provider || "-";
      
      case 'total_salary':
        return String(item.total_salary || "-");
      
      case 'iqama_expiry_date_g':
      case 'arrival_date':
      case 'mobilization_date':
      case 'demobilization_date':
      case 'entry_before_date':
        return formatDate(item[column.key]);
      
      case 'idle_aging':
      case 'aging':
        return item[column.key]?.toString() || "-";
      
      case 'emergency_leave_status':
        return renderEreStatus(item);
      
      case 'action':
        return checkAccess("DEMOBILIZATION", "ere-edit") ? renderActionButton(item, handleEreClick, handleEreExtendClick) : null;
      
      default:
        return null;
    }
  }, [handleEreClick, handleEreExtendClick, dashboardData]);

  // Optimized download handler (using SmartDownloadPagination's built-in download)
  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({
      msg: "Excel Downloaded",
      color: "success",
    }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({
      msg: error.message || "Download failed",
    }));
  }, [dispatch]);

  // UpdateDataByDate functionality (preserved from original)
  const updateDataByDate = useCallback(async (currentData) => {
    const filteredData = currentData?.filter(
      (data) =>
        new Date() >= new Date(data?.demobilization_date) &&
        data.emergency_leave_status === ERE_STATUS_TYPES.ERE_ISSUED
    );

    if (filteredData && filteredData.length > 0) {
      try {
        const response = await nodeinstance.put(
          ERE_CONFIG.UPDATE_ERE_ENDPOINT,
          filteredData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (response.status === 200) {
          dispatch(
            setAppError({ 
              msg: "Data Updated Successfully", 
              color: "success" 
            })
          );
          setTrigger(!trigger);
        }
      } catch (error) {
        console.error("Error updating ERE data:", error);
        dispatch(setAppError({
          msg: error.message || "Failed to update ERE data"
        }));
      }
    }
  }, [token, dispatch, setTrigger, trigger]);

  // Handle data load with auto-update logic
  const handleDataLoadWithUpdate = useCallback((data, pagination) => {
    handleDataLoad(data, pagination);
    
    // Auto-update logic (preserved from original)
    if (data && data.length > 0) {
      updateDataByDate(data);
    }
  }, [handleDataLoad, updateDataByDate]);
  return (
    <div className="p-2">
      <SmartDownloadPagination
        // API Configuration
        apiEndpoint={ERE_CONFIG.API_ENDPOINT}
        downloadEndpoint={ERE_CONFIG.DOWNLOAD_ENDPOINT}
        token={token}
        
        // Table Configuration
        columns={ERE_COLUMNS}
        searchableFields={ERE_SEARCHABLE_FIELDS}
        layoutType="table" // Use table layout instead of grid
        enableHighlighting={true}
        
        // Pagination Configuration
        defaultPageSize={ERE_CONFIG.DEFAULT_PAGE_SIZE}
        pageSizeOptions={ERE_CONFIG.PAGE_SIZE_OPTIONS}
        
        // Download Configuration
        downloadFilename={ERE_CONFIG.DOWNLOAD_FILENAME}
        enableDownload={true}
        
        // Search Configuration
        enableSearch={true}
        searchPlaceholder={ERE_CONFIG.SEARCH_PLACEHOLDER}
        searchDebounceMs={ERE_CONFIG.DEBOUNCE_MS}
        
        // Trigger for cache invalidation
        trigger={trigger}
        
        // Customization
        renderCustomCell={renderCustomCell}
        
        // Event Handlers
        onDataLoad={handleDataLoadWithUpdate}
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}
        
        // Additional Props
        noDataMessage={ERE_CONFIG.NO_DATA_MESSAGE}
        className="ere-table"
      />

      {/* Popups (preserved from original) */}
      {popupState.ere && popUpData && (
        <ErePopup
          PopUp={popupState.ere}
          setPopUp={closeErePopup}
          popUpData={popUpData}
          setTrigger={setTrigger}
          trigger={trigger}
          routeType="Emergency_leave"
          isEdit={
            popUpData?.emergency_leave_status === ERE_STATUS_TYPES.ON_ERE_PAYMENT_DONE ||
            popUpData?.emergency_leave_status === ERE_STATUS_TYPES.ERE_ISSUED ||
            popUpData?.emergency_leave_status === ERE_STATUS_TYPES.ON_ERE
          }
          onEre={popUpData?.emergency_leave_status === ERE_STATUS_TYPES.ON_ERE}
          mainData={list?.data}
          Cancelshow={
            popUpData.emergency_leave_status != null ||
            popUpData?.emergency_leave_status === ERE_STATUS_TYPES.ON_ERE_PAYMENT_DONE ||
            popUpData?.emergency_leave_status === ERE_STATUS_TYPES.ON_ERE
          }
          issuedState={
            popUpData?.emergency_leave_status === ERE_STATUS_TYPES.ERE_ISSUED ||
            popUpData?.emergency_leave_status === ERE_STATUS_TYPES.ON_ERE
              ? false
              : true
          }
        />
      )}
      
      {popupState.ereExtension && popUpData && (
        <EreExtensionPopUp
          PopUp={popupState.ereExtension}
          setPopUp={closeEreExtensionPopup}
          popUpData={popUpData}
          setTrigger={setTrigger}
          routeType="Emergency_leave"
          trigger={trigger}
          isEdit={
            popUpData.emergency_leave_status === ERE_STATUS_TYPES.ERE_EXTENSION_PAYMENT_DONE ||
            popUpData.emergency_leave_status === ERE_STATUS_TYPES.ON_ERE_EXTENSION_ISSUED
          }
          mainData={list?.data}
          Cancelshow={popUpData.emergency_leave_status != null}
        />
      )}
      
      {popupState.idle && (
        <IdlePopUp />
      )}
    </div>
  );
};

EreTabList.displayName = 'EreTabList';

export default EreTabList;
